import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import DashboardStats from './components/DashboardStats';
import UserManagement from './components/UserManagement';
import PropertyManagement from './components/PropertyManagement';
import AppointmentManagement from './components/AppointmentManagement';
import ContentManagement from './components/ContentManagement';
import NotificationCenter from './components/NotificationCenter';
import ActivityMonitor from './components/ActivityMonitor';
import ModelAdminDashboard from './components/ModelAdminDashboard';
import ConversionFunnelDashboard from './components/ConversionFunnelDashboard';
import EmailCampaignPanel from './components/EmailCampaignPanel';
import PromoVideoAdminPanel from './components/PromoVideoAdminPanel';
import ProfessionalManagement from './components/ProfessionalManagement';
import ApplicationReviewPanel from './components/ApplicationReviewPanel';
import SmsNotificationPreferences from './components/SmsNotificationPreferences';
import EscalationPanel from './components/EscalationPanel';
import BatchReminderTool from './components/BatchReminderTool';
import AdminNotificationsPanel from './components/AdminNotificationsPanel';
import LeadScoringConfigPanel from './components/LeadScoringConfigPanel';
import MatterportToursAdmin from './components/MatterportToursAdmin';
import DataArchivalPolicyManager from './components/DataArchivalPolicyManager';
import GDPRComplianceExports from './components/GDPRComplianceExports';
import AuditLogsPanel from './components/AuditLogsPanel';
import ComplianceAuditReport from './components/ComplianceAuditReport';
import NotaireAccessRequestsPanel from './components/NotaireAccessRequestsPanel';
import AdminNotaryAssignment from './components/AdminNotaryAssignment';
import EscrowManagementPanel from './components/EscrowManagementPanel';
import PaystackVerificationPanel from './components/PaystackVerificationPanel';
import FinanceDisputeAnalyticsPanel from './components/FinanceDisputeAnalyticsPanel';
import FinanceCommissionDashboard from './components/FinanceCommissionDashboard';
import EscrowActionLog from './components/EscrowActionLog';
import escalationService from '../../services/escalationService';
import { subscribePooled } from '../../utils/realtimePoolManager';
import Skeleton from '../../components/ui/Skeleton';
import {
  Users,
  Home,
  Calendar,
  Edit3,
  Bell,
  Activity,
  BarChart3,
  Settings,
  LogOut,
  Box,
  TrendingUp,
  Mail,
  Video,
  Briefcase,
  ClipboardCheck,
  MessageSquare,
  AlertTriangle,
  Send,
  BellRing,
  Flame,
  SlidersHorizontal,
  Glasses,
  Archive,
  Shield,
  ClipboardList,
  FileCheck,
  UserCheck,
  Lock,
  CreditCard,
  PieChart,
  DollarSign,
  FileSearch,
  Globe,
  FileText
} from 'lucide-react';
import FinanceOperationsTab from './components/FinanceOperationsTab';
import PlatformMetricsDashboard from './components/PlatformMetricsDashboard';
import BatchReconciliationDashboard from './components/BatchReconciliationDashboard';
import MultiCurrencyPayoutManager from './components/MultiCurrencyPayoutManager';
import AutoScheduledPayoutsPanel from './components/AutoScheduledPayoutsPanel';
import SettlementReportGenerator from './components/SettlementReportGenerator';
import TaxFormGenerator from './components/TaxFormGenerator';
import RealTimePayoutTracker from './components/RealTimePayoutTracker';
import FundsLiquidityDashboard from './components/FundsLiquidityDashboard';
import KYCVerificationModule from './components/KYCVerificationModule';
import FailedTransferRetryQueue from './components/FailedTransferRetryQueue';
import PaystackAnomalyDetectionPanel from './components/PaystackAnomalyDetectionPanel';
import EscrowDisputeEscalation from '../../pages/escrow-dispute-escalation';
import DisputeResolutionPanel from './components/DisputeResolutionPanel';



const AdminDashboard = () => {
  const { user, userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [pendingApplicationsCount, setPendingApplicationsCount] = useState(0);
  const [urgentEscalationsCount, setUrgentEscalationsCount] = useState(0);
  const [hotLeadsCount, setHotLeadsCount] = useState(0);
  const [pendingNotaireRequestsCount, setPendingNotaireRequestsCount] = useState(0);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalProperties: 0,
    totalAppointments: 0,
    totalNotifications: 0,
    recentActivity: []
  });

  // Check if user is admin
  const isAdmin = userProfile?.role === 'admin';

  // Admin navigation items
  const navItems = [
    { id: 'dashboard', label: 'Tableau de Bord', icon: BarChart3 },
    { id: 'funnel', label: 'Entonnoir Conversion', icon: TrendingUp },
    { id: 'users', label: 'Gestion Utilisateurs', icon: Users },
    { id: 'properties', label: 'Gestion Propriétés', icon: Home },
    { id: 'appointments', label: 'RDV Notaire', icon: Calendar },
    { id: 'professionals', label: 'Professionnels', icon: Briefcase },
    { id: 'applications', label: 'Candidatures', icon: ClipboardCheck },
    { id: 'notaire-requests', label: 'Accès Notaires', icon: FileCheck },
    { id: 'notaire-assignment', label: 'Attribution Notaires', icon: UserCheck },
    { id: 'escrow', label: 'Gestion Séquestre', icon: Lock },
    { id: 'funds-liquidity', label: 'Fonds & Liquidités', icon: DollarSign },
    { id: 'kyc-module', label: 'Vérification KYC', icon: UserCheck },
    { id: 'failed-transfers', label: 'Virements Échoués', icon: AlertTriangle },
    { id: 'finance-operations', label: 'Finance Operations', icon: AlertTriangle },
    { id: 'finance-paystack', label: 'Vérification Paystack', icon: CreditCard },
    { id: 'finance-analytics', label: 'Analytiques Finance', icon: PieChart },
    { id: 'finance-commissions', label: 'Commissions', icon: DollarSign },
    { id: 'escalations', label: 'Escalades', icon: AlertTriangle },
    { id: 'batch-reminders', label: 'Rappels en Lot', icon: Send },
    { id: 'content', label: 'Gestion Contenu', icon: Edit3 },
    { id: 'campaigns', label: 'Campagnes Email', icon: Mail },
    { id: 'promo-videos', label: 'Vidéos Promotionnelles', icon: Video },
    { id: 'matterport-tours', label: 'Visites Immersives', icon: Glasses },
    { id: 'admin-notifications', label: 'Leads & Alertes', icon: BellRing },
    { id: 'lead-scoring-config', label: 'Config Scoring Leads', icon: SlidersHorizontal },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'sms-preferences', label: 'Notifications SMS', icon: MessageSquare },
    { id: 'activity', label: 'Activité Système', icon: Activity },
    { id: 'models3d', label: 'Modèles 3D', icon: Box },
    { id: 'archival-policies', label: 'Politiques d\'Archivage', icon: Archive },
    { id: 'gdpr-exports', label: 'Exports RGPD / Conformité', icon: Shield },
    { id: 'audit-logs', label: 'Journaux d\'Audit', icon: ClipboardList },
    { id: 'compliance-audit', label: 'Rapport Conformité', icon: FileSearch },
    { id: 'platform-metrics', label: 'Métriques Plateforme', icon: Activity },
    { id: 'batch-reconciliation', label: 'Réconciliation en Lot', icon: DollarSign },
    { id: 'multicurrency-payouts', label: 'Paiements Multi-Devises', icon: Globe },
    { id: 'scheduled-payouts', label: 'Paiements Automatisés', icon: Calendar },
    { id: 'settlement-reports', label: 'Rapports de Règlement', icon: FileText },
    { id: 'tax-forms', label: 'Formulaires Fiscaux', icon: FileText },
    { id: 'payout-tracker', label: 'Tracker Paiements Live', icon: TrendingUp },
    { id: 'paystack-anomalies', label: 'Anomalies Paystack', icon: AlertTriangle },
    { id: 'dispute-escalation', label: 'Escalades Litiges', icon: AlertTriangle },
    { id: 'dispute-resolution', label: 'Clôture Litiges', icon: Shield },
  ];

  // Fetch pending notaire requests count
  const fetchPendingNotaireRequestsCount = async () => {
    try {
      const { count } = await supabase
        ?.from('notaire_access_requests')
        ?.select('*', { count: 'exact', head: true })
        ?.eq('status', 'pending');
      setPendingNotaireRequestsCount(count || 0);
    } catch (error) {
      console.error('Error fetching pending notaire requests count:', error);
    }
  };

  // Fetch pending applications count
  const fetchPendingApplicationsCount = async () => {
    try {
      const { count } = await supabase
        ?.from('professional_applications')
        ?.select('*', { count: 'exact', head: true })
        ?.in('status', ['submitted', 'under_review']);
      setPendingApplicationsCount(count || 0);
    } catch (error) {
      console.error('Error fetching pending applications count:', error);
    }
  };

  // Fetch urgent escalations count
  const fetchUrgentEscalationsCount = async () => {
    try {
      const { data } = await escalationService?.getEscalationStats();
      setUrgentEscalationsCount(data?.total || 0);
    } catch (error) {
      console.error('Error fetching escalation stats:', error);
    }
  };

  // Real-time subscription for professional applications
  useEffect(() => {
    if (!isAdmin) return;

    fetchPendingApplicationsCount();
    fetchUrgentEscalationsCount();
    fetchPendingNotaireRequestsCount();

    // Fetch initial hot leads count
    const fetchHotLeads = async () => {
      try {
        const { data } = await supabase
          ?.from('property_inquiries')
          ?.select('id, status')
          ?.eq('status', 'new')
          ?.order('created_at', { ascending: false })
          ?.limit(50);
        setHotLeadsCount(data?.length || 0);
      } catch (_) {}
    };
    fetchHotLeads();

    // Pooled + debounced subscription — coalesces rapid admin-panel changes
    // into a single batch flush (400 ms window) to prevent re-render storms
    const unsubscribe = subscribePooled({
      channelName: 'admin-pending-applications',
      listeners: [
        { event: '*', schema: 'public', table: 'professional_applications' },
        { event: '*', schema: 'public', table: 'professional_bookings' },
        { event: 'INSERT', schema: 'public', table: 'property_inquiries' },
        { event: '*', schema: 'public', table: 'notaire_access_requests' },
      ],
      debounceMs: 400,
      callback: (batch) => {
        let refreshApplications = false;
        let refreshEscalations = false;
        let refreshNotaireRequests = false;
        let newInquiries = 0;

        batch?.forEach((payload) => {
          const table = payload?.table;
          if (table === 'professional_applications') {
            refreshApplications = true;
          } else if (table === 'professional_bookings') {
            refreshEscalations = true;
          } else if (table === 'property_inquiries' && payload?.eventType === 'INSERT') {
            newInquiries += 1;
            refreshEscalations = true;
          } else if (table === 'notaire_access_requests') {
            refreshNotaireRequests = true;
          }
        });

        if (refreshApplications) fetchPendingApplicationsCount();
        if (refreshEscalations) fetchUrgentEscalationsCount();
        if (newInquiries > 0) setHotLeadsCount((c) => c + newInquiries);
        if (refreshNotaireRequests) fetchPendingNotaireRequestsCount();
      },
    });

    return () => {
      unsubscribe();
    };
  }, [isAdmin]);

  // Fetch dashboard statistics
  const fetchDashboardStats = async () => {
    try {
      setLoading(true);

      // Parallel fetch for better performance
      const [
        usersResponse,
        propertiesResponse,
        appointmentsResponse,
        notificationsResponse,
        activityResponse
      ] = await Promise.all([
        supabase?.from('user_profiles')?.select('*', { count: 'exact', head: true }),
        supabase?.from('properties')?.select('*', { count: 'exact', head: true }),
        supabase?.from('appointments')?.select('*', { count: 'exact', head: true }),
        supabase?.from('notifications')?.select('*', { count: 'exact', head: true }),
        supabase?.from('activity_logs')?.select('*')?.order('created_at', { ascending: false })?.limit(10)
      ]);

      setStats({
        totalUsers: usersResponse?.count || 0,
        totalProperties: propertiesResponse?.count || 0,
        totalAppointments: appointmentsResponse?.count || 0,
        totalNotifications: notificationsResponse?.count || 0,
        recentActivity: activityResponse?.data || []
      });
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchDashboardStats();
    }
  }, [isAdmin]);

  // Handle admin logout
  const handleLogout = async () => {
    try {
      const { error } = await supabase?.auth?.signOut();
      if (error) {
        alert('Erreur lors de la déconnexion: ' + error?.message);
      }
    } catch (error) {
      console.error('Logout error:', error);
      alert('Erreur lors de la déconnexion. Veuillez réessayer.');
    }
  };

  // Redirect non-admin users
  if (!loading && !isAdmin) {
    return (
      <div className="min-h-screen bg-red-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="text-red-600 mb-4">
            <Settings className="w-16 h-16 mx-auto" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Accès Administrateur Requis
          </h1>
          <p className="text-gray-600 mb-6">
            Cette interface est réservée aux administrateurs du système.
            Veuillez contacter votre administrateur pour obtenir l'accès.
          </p>
          <button
            onClick={handleLogout}
            className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors"
          >
            Se Déconnecter
          </button>
        </div>
      </div>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <DashboardStats stats={stats} loading={loading} onRefresh={fetchDashboardStats} />;
      case 'users':
        return <UserManagement onStatsUpdate={fetchDashboardStats} />;
      case 'properties':
        return <PropertyManagement onStatsUpdate={fetchDashboardStats} />;
      case 'appointments':
        return <AppointmentManagement onStatsUpdate={fetchDashboardStats} />;
      case 'professionals':
        return <ProfessionalManagement />;
      case 'applications':
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">Gérez les candidatures directement ici ou ouvrez le tableau de bord dédié pour une vue centralisée complète.</p>
              <button
                onClick={() => navigate('/admin-applications-dashboard')}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors flex-shrink-0"
              >
                <ClipboardCheck className="w-4 h-4" />
                Ouvrir le dashboard dédié
              </button>
            </div>
            <ApplicationReviewPanel />
          </div>
        );
      case 'notaire-requests':
        return <NotaireAccessRequestsPanel />;
      case 'notaire-assignment':
        return <AdminNotaryAssignment />;
      case 'escrow':
        return <EscrowManagementPanel />;
      case 'finance-paystack':
        return <PaystackVerificationPanel />;
      case 'finance-analytics':
        return (
          <div className="space-y-8">
            <FinanceDisputeAnalyticsPanel />
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <EscrowActionLog />
            </div>
          </div>
        );
      case 'finance-commissions':
        return <FinanceCommissionDashboard />;
      case 'finance-operations':
        return <FinanceOperationsTab />;
      case 'escalations':
        return <EscalationPanel />;
      case 'batch-reminders':
        return <BatchReminderTool />;
      case 'content':
        return <ContentManagement onStatsUpdate={fetchDashboardStats} />;
      case 'campaigns':
        return <EmailCampaignPanel />;
      case 'promo-videos':
        return <PromoVideoAdminPanel />;
      case 'matterport-tours':
        return <MatterportToursAdmin />;
      case 'admin-notifications':
        return <AdminNotificationsPanel />;
      case 'lead-scoring-config':
        return <LeadScoringConfigPanel />;
      case 'notifications':
        return <NotificationCenter onStatsUpdate={fetchDashboardStats} />;
      case 'sms-preferences':
        return <SmsNotificationPreferences />;
      case 'activity':
        return <ActivityMonitor activities={stats?.recentActivity} onRefresh={fetchDashboardStats} />;
      case 'models3d':
        return <ModelAdminDashboard onBack={() => setActiveTab('dashboard')} />;
      case 'funnel':
        return <ConversionFunnelDashboard />;
      case 'archival-policies':
        return <DataArchivalPolicyManager />;
      case 'gdpr-exports':
        return <GDPRComplianceExports />;
      case 'audit-logs':
        return <AuditLogsPanel />;
      case 'compliance-audit':
        return (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <ComplianceAuditReport />
          </div>
        );
      case 'platform-metrics':
        return (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <PlatformMetricsDashboard />
          </div>
        );
      case 'batch-reconciliation':
        return <BatchReconciliationDashboard />;
      case 'multicurrency-payouts':
        return (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <MultiCurrencyPayoutManager />
          </div>
        );
      case 'scheduled-payouts':
        return (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <AutoScheduledPayoutsPanel />
          </div>
        );
      case 'settlement-reports':
        return (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <SettlementReportGenerator />
          </div>
        );
      case 'tax-forms':
        return (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <TaxFormGenerator />
          </div>
        );
      case 'payout-tracker':
        return (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <RealTimePayoutTracker />
          </div>
        );
      case 'funds-liquidity':
        return (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <FundsLiquidityDashboard />
          </div>
        );
      case 'kyc-module':
        return (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <KYCVerificationModule />
          </div>
        );
      case 'failed-transfers':
        return (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <FailedTransferRetryQueue />
          </div>
        );
      case 'paystack-anomalies':
        return (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <PaystackAnomalyDetectionPanel />
          </div>
        );
      case 'dispute-escalation':
        return <EscrowDisputeEscalation />;
      case 'dispute-resolution':
        return <DisputeResolutionPanel />;
      default:
        return <DashboardStats stats={stats} loading={loading} onRefresh={fetchDashboardStats} />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex">
        {/* Sidebar skeleton */}
        <div className="w-64 bg-white shadow-lg flex-shrink-0">
          <div className="p-6 border-b border-border space-y-2">
            <Skeleton className="h-6 w-36" />
            <Skeleton className="h-4 w-28" />
          </div>
          <div className="mt-6 space-y-1 px-3">
            {[...Array(8)]?.map((_, i) => (
              <div key={i} className="flex items-center gap-3 px-3 py-3">
                <Skeleton className="h-5 w-5 rounded flex-shrink-0" />
                <Skeleton className="h-4 flex-1" />
              </div>
            ))}
          </div>
        </div>
        {/* Main content skeleton */}
        <div className="flex-1 p-8 space-y-6">
          {/* Header card */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div className="space-y-2">
                <Skeleton className="h-7 w-48" />
                <Skeleton className="h-4 w-72" />
              </div>
              <Skeleton className="h-10 w-10 rounded-full" />
            </div>
          </div>
          {/* Stats grid skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)]?.map((_, i) => (
              <div key={i} className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="space-y-2 flex-1">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-7 w-20" />
                  </div>
                  <Skeleton className="h-12 w-12 rounded-lg flex-shrink-0" />
                </div>
                <Skeleton className="h-4 w-24" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Interface Administration - Système Immobilier</title>
        <meta name="description" content="Interface administrative complète pour la gestion du système immobilier - utilisateurs, propriétés, rendez-vous et contenu" />
      </Helmet>
      
      <div className="min-h-screen bg-gray-50 flex">
        {/* Sidebar Navigation */}
        <div className="w-64 bg-white shadow-lg">
          <div className="p-6 border-b border-gray-200">
            <h1 className="text-xl font-bold text-gray-900">
              Admin Dashboard
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Interface Administration
            </p>
          </div>
          
          <nav className="mt-6">
            {navItems?.map((item) => {
              const ItemIcon = item?.icon;
              const isPendingApplicationsTab = item?.id === 'applications';
              const isEscalationsTab = item?.id === 'escalations';
              const isAdminNotifTab = item?.id === 'admin-notifications';
              const isNotaireRequestsTab = item?.id === 'notaire-requests';
              const showBadge = isPendingApplicationsTab && pendingApplicationsCount > 0;
              const showEscalationBadge = isEscalationsTab && urgentEscalationsCount > 0;
              const showHotLeadsBadge = isAdminNotifTab && hotLeadsCount > 0;
              const showNotaireBadge = isNotaireRequestsTab && pendingNotaireRequestsCount > 0;
              return (
                <button
                  key={item?.id}
                  onClick={() => setActiveTab(item?.id)}
                  className={`w-full flex items-center px-6 py-3 text-left hover:bg-gray-50 transition-colors ${
                    activeTab === item?.id
                      ? 'bg-blue-50 border-r-2 border-blue-600 text-blue-700' :'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <ItemIcon className={`w-5 h-5 mr-3 flex-shrink-0 ${isEscalationsTab && urgentEscalationsCount > 0 ? 'text-red-500' : ''} ${isAdminNotifTab && hotLeadsCount > 0 ? 'text-orange-500' : ''} ${isNotaireRequestsTab && pendingNotaireRequestsCount > 0 ? 'text-yellow-600' : ''}`} />
                  <span className="font-medium flex-1">{item?.label}</span>
                  {showBadge && (
                    <span className="ml-2 flex-shrink-0 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-bold animate-pulse">
                      {pendingApplicationsCount > 99 ? '99+' : pendingApplicationsCount}
                    </span>
                  )}
                  {showEscalationBadge && (
                    <span className="ml-2 flex-shrink-0 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-600 text-white text-xs font-bold animate-pulse">
                      {urgentEscalationsCount > 99 ? '99+' : urgentEscalationsCount}
                    </span>
                  )}
                  {showHotLeadsBadge && (
                    <span className="ml-2 flex-shrink-0 inline-flex items-center gap-0.5 min-w-[20px] h-5 px-1.5 rounded-full bg-orange-500 text-white text-xs font-bold animate-pulse">
                      <Flame className="w-2.5 h-2.5" />
                      {hotLeadsCount > 99 ? '99+' : hotLeadsCount}
                    </span>
                  )}
                  {showNotaireBadge && (
                    <span className="ml-2 flex-shrink-0 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-yellow-500 text-white text-xs font-bold animate-pulse">
                      {pendingNotaireRequestsCount > 99 ? '99+' : pendingNotaireRequestsCount}
                    </span>
                  )}
                </button>
              );
            })}
            
            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="w-full flex items-center px-6 py-3 text-left text-red-600 hover:bg-red-50 transition-colors mt-4 border-t border-gray-200"
            >
              <LogOut className="w-5 h-5 mr-3" />
              <span className="font-medium">Se Déconnecter</span>
            </button>
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-hidden">
          <div className="h-full overflow-y-auto">
            <div className="p-8">
              {/* Header */}
              <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      {navItems?.find(item => item?.id === activeTab)?.label || 'Tableau de Bord'}
                    </h2>
                    <p className="text-gray-600 mt-1">
                      Interface administrative complète pour la gestion du système immobilier
                    </p>
                  </div>
                  <div className="flex items-center space-x-4">
                    {pendingNotaireRequestsCount > 0 && activeTab !== 'notaire-requests' && (
                      <button
                        onClick={() => setActiveTab('notaire-requests')}
                        className="flex items-center space-x-2 bg-yellow-50 hover:bg-yellow-100 text-yellow-800 px-4 py-2 rounded-lg transition-colors border border-yellow-200"
                      >
                        <FileCheck className="w-4 h-4" />
                        <span className="text-sm font-medium">
                          {pendingNotaireRequestsCount} demande{pendingNotaireRequestsCount > 1 ? 's' : ''} notaire en attente
                        </span>
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-yellow-500 text-white text-xs font-bold">
                          {pendingNotaireRequestsCount > 99 ? '99+' : pendingNotaireRequestsCount}
                        </span>
                      </button>
                    )}
                    {pendingApplicationsCount > 0 && activeTab !== 'applications' && (
                      <button
                        onClick={() => setActiveTab('applications')}
                        className="flex items-center space-x-2 bg-red-50 hover:bg-red-100 text-red-700 px-4 py-2 rounded-lg transition-colors border border-red-200"
                      >
                        <ClipboardCheck className="w-4 h-4" />
                        <span className="text-sm font-medium">
                          {pendingApplicationsCount} candidature{pendingApplicationsCount > 1 ? 's' : ''} en attente
                        </span>
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-500 text-white text-xs font-bold">
                          {pendingApplicationsCount > 99 ? '99+' : pendingApplicationsCount}
                        </span>
                      </button>
                    )}
                    {urgentEscalationsCount > 0 && activeTab !== 'escalations' && (
                      <button
                        onClick={() => setActiveTab('escalations')}
                        className="flex items-center space-x-2 bg-orange-50 hover:bg-orange-100 text-orange-700 px-4 py-2 rounded-lg transition-colors border border-orange-200"
                      >
                        <AlertTriangle className="w-4 h-4" />
                        <span className="text-sm font-medium">
                          {urgentEscalationsCount} escalade{urgentEscalationsCount > 1 ? 's' : ''} urgente{urgentEscalationsCount > 1 ? 's' : ''}
                        </span>
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-orange-500 text-white text-xs font-bold">
                          {urgentEscalationsCount > 99 ? '99+' : urgentEscalationsCount}
                        </span>
                      </button>
                    )}
                    {hotLeadsCount > 0 && activeTab !== 'admin-notifications' && (
                      <button
                        onClick={() => setActiveTab('admin-notifications')}
                        className="flex items-center space-x-2 bg-amber-50 hover:bg-amber-100 text-amber-700 px-4 py-2 rounded-lg transition-colors border border-amber-200"
                      >
                        <Flame className="w-4 h-4" />
                        <span className="text-sm font-medium">
                          {hotLeadsCount} lead{hotLeadsCount > 1 ? 's' : ''} chaud{hotLeadsCount > 1 ? 's' : ''}
                        </span>
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-500 text-white text-xs font-bold">
                          {hotLeadsCount > 99 ? '99+' : hotLeadsCount}
                        </span>
                      </button>
                    )}
                    <div className="text-right">
                      <p className="text-sm text-gray-600">Connecté en tant que</p>
                      <p className="font-medium text-gray-900">{userProfile?.full_name}</p>
                    </div>
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-medium">
                        {userProfile?.full_name?.charAt(0)?.toUpperCase()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tab Content */}
              {renderTabContent()}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminDashboard;